import React, { useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip,
} from 'recharts';
import { motion } from 'framer-motion';
import { BasicStackedTooltip } from './CustomLineTooltip';
import { RegulationId } from '../types/dashboard.types';
import { getComplianceSubcategoryColor } from '../../../../utilities/colorUtilities';
import { useChartView } from '../DashboardContext';
import { useComplianceNavigator } from '../../../../hooks/useComplianceNavigator';
import { tailwindColors } from '../../../../../tailwind.config';
import GenericModal from '../../../ui/GenericModal';
import { filterDataByScope } from '../../../../utilities/dateUtilities';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { FundSummaryWithTypes } from '../types/chart.types';
import { sortFundNames } from '../../../../utilities/customSorting';
import { addComponentToPdfExport } from '../../../../redux/fileExport/actions';
import usePngFromComponent from '../../../../hooks/usePngFromComponent';
import { PdfComponentType } from '../../../../types/redux/pdfExports/FileExportsStore';
import { useDispatch } from 'react-redux';
import { StatusPill, StatusPillGroup } from './StatusPill';

export interface ComplianceBarChartProps {
  data: {
    date: string;
    data: Record<string, FundSummaryWithTypes>;
  }[];
  regulation: RegulationId;
  subCategoryFilters: string[];
  allDates: string[];
  daysInScope: number;
  animationDelay?: number;
  height?: number;
}

type NavEntry = {
  date: string;
  Fail: number;
  Alert: number;
  Pass: number;
  fullData: Record<string, FundSummaryWithTypes>;
};

const ComplianceBarChart: React.FC<ComplianceBarChartProps> = ({
  data,
  animationDelay = 0,
  height = 250,
  subCategoryFilters,
  regulation,
  daysInScope,
}) => {
  const { hoveredFundId: hoveredFundName } = useChartView();
  const { ref, handleDownload } = usePngFromComponent();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: 'dashboard_compliance_bar_chart',
        handler: handleDownload,
        type: PdfComponentType.GENERAL_COMPONENT_IMAGE,
        title: '',
        dontMoveToNewPage: true,
        specificYPosition: undefined,
        specificXPosition: undefined,
        alignment: undefined,
        dontIncrementYPosition: false,
        specificHeight: 55,
        groupOrder: 1,
        groupName: 'compliance_dashboard',
      }),
    );
  }, [ref]);

  const dataInScope = filterDataByScope(data, daysInScope);

  const [navData, setNavData] = React.useState<NavEntry>();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [hoverEntry, setHoverEntry] = React.useState<NavEntry | undefined>();

  const flattenedData = dataInScope.map((entry) => {
    let totalFailCount = 0;
    let totalAlertCount = 0;
    let totalPassCount = 0;

    // Only process data if it exists and has content
    if (entry.data && Object.keys(entry.data).length > 0) {
      subCategoryFilters.forEach((subcategory) => {
        totalFailCount += entry.data[subcategory]?.Fail?.count ?? 0;
        totalAlertCount += entry.data[subcategory]?.Alert?.count ?? 0;
        totalPassCount += entry.data[subcategory]?.Pass?.count ?? 0;
      });
    }

    return {
      date: entry.date,
      Fail: totalFailCount,
      Alert: totalAlertCount,
      Pass: totalPassCount,
      fullData: entry.data || {},
    };
  });

  // Always render the chart, even with empty data, to show the timeline

  return (
    <div ref={ref} className="relative">
      <motion.div
        key="fund-bar"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={flattenedData}
            margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
            onMouseMove={(e: any) => {
              const payload = e?.activePayload?.[0]?.payload;
              if (payload) {
                setHoverEntry({
                  date: payload.date,
                  Fail: payload.Fail,
                  Alert: payload.Alert,
                  Pass: payload.Pass,
                  fullData: payload.fullData,
                });
              }
            }}
            onClick={() => {
              if (hoverEntry) {
                setNavData(hoverEntry);
                setModalOpen(true);
              }
            }}
          >
            <CartesianGrid
              stroke="#e5e7eb"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              angle={-45}
              textAnchor="end"
              height={50}
              tick={{ fontSize: 12, fill: '#1f2937' }}
              stroke="#6b7280"
              tickFormatter={(value: string) => {
                const date = new Date(value);
                return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(
                  date.getDate(),
                ).padStart(2, '0')}`;
              }}
            />
            <YAxis
              allowDecimals={false}
              domain={[0, 'dataMax + 1']}
              tick={{ fontSize: 12, fill: '#374151' }}
              stroke="#6b7280"
              width={36}
            />

            <Tooltip content={<BasicStackedTooltip />} />
            <Bar
              key="Fail"
              dataKey="Fail"
              name="Fail"
              stackId="stacked"
              fill={tailwindColors.fail}
              animationBegin={animationDelay}
              style={{ cursor: 'pointer' }}
            >
              {flattenedData.map((entry, index) => {
                const fundNames = Object.values(entry.fullData || {}).flatMap(
                  (subcategoryData) => [
                    ...subcategoryData.Fail.funds.map((f) => f.fund_name),
                    ...subcategoryData.Alert.funds.map((f) => f.fund_name),
                    ...subcategoryData.Pass.funds.map((f) => f.fund_name),
                  ],
                );
                const isDimmed =
                  hoveredFundName && !fundNames.includes(hoveredFundName);

                return (
                  <Cell
                    key={`Fail-${index}`}
                    fill={tailwindColors.fail}
                    fillOpacity={hoveredFundName ? (isDimmed ? 0.25 : 1) : 1}
                    onClick={() => {
                      setNavData(entry);
                      setModalOpen(true);
                    }}
                  />
                );
              })}
            </Bar>

            <Bar
              key="Alert"
              dataKey="Alert"
              name="Alert"
              stackId="stacked"
              fill={tailwindColors.alert}
              animationBegin={animationDelay + 1}
              style={{ cursor: 'pointer' }}
            >
              {flattenedData.map((entry, index) => {
                const fundNames = Object.values(entry.fullData || {}).flatMap(
                  (subcategoryData) => [
                    ...subcategoryData.Fail.funds.map((f) => f.fund_name),
                    ...subcategoryData.Alert.funds.map((f) => f.fund_name),
                    ...subcategoryData.Pass.funds.map((f) => f.fund_name),
                  ],
                );
                const isDimmed =
                  hoveredFundName && !fundNames.includes(hoveredFundName);

                return (
                  <Cell
                    key={`Alert-${index}`}
                    fill={tailwindColors.alert}
                    fillOpacity={hoveredFundName ? (isDimmed ? 0.25 : 1) : 1}
                    onClick={() => {
                      setNavData(entry);
                      setModalOpen(true);
                    }}
                  />
                );
              })}
            </Bar>

            <Bar
              key="Pass"
              dataKey="Pass"
              name="Pass"
              stackId="stacked"
              fill={tailwindColors.pass}
              animationBegin={animationDelay + 2}
              style={{ cursor: 'pointer' }}
            >
              {flattenedData.map((entry, index) => {
                const fundNames = Object.values(entry.fullData || {}).flatMap(
                  (subcategoryData) => [
                    ...subcategoryData.Fail.funds.map((f) => f.fund_name),
                    ...subcategoryData.Alert.funds.map((f) => f.fund_name),
                    ...subcategoryData.Pass.funds.map((f) => f.fund_name),
                  ],
                );
                const isDimmed =
                  hoveredFundName && !fundNames.includes(hoveredFundName);

                return (
                  <Cell
                    key={`Pass-${index}`}
                    fill={tailwindColors.pass}
                    fillOpacity={hoveredFundName ? (isDimmed ? 0.25 : 1) : 1}
                    onClick={() => {
                      setNavData(entry);
                      setModalOpen(true);
                    }}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {navData && (
        <GenericModal
          isOpen={modalOpen}
          setIsOpen={setModalOpen}
          title={
            <header className="flex w-fit flex-wrap items-baseline gap-6 text-[20px]">
              <h1>
                {(() => {
                  const date = new Date(navData.date);
                  const dayOfWeek = date.toLocaleDateString('en-US', {
                    weekday: 'long',
                  });
                  return `${dayOfWeek}, ${navData.date}`;
                })()}
              </h1>
              <span className="text-[16px] font-medium text-gray-500 underline underline-offset-4">
                {navData.Fail + navData.Alert + navData.Pass} Funds
              </span>
              <StatusPillGroup
                data={{
                  Fail: navData.Fail,
                  Alert: navData.Alert,
                  Pass: navData.Pass,
                }}
              />
            </header>
          }
          className="max-h-[80vh] w-full max-w-3xl"
        >
          <div className="max-h-[65vh] overflow-y-auto">
            <NavEntryComponent navEntry={navData} regulation={regulation} />
          </div>
        </GenericModal>
      )}
    </div>
  );
};

export default ComplianceBarChart;

interface NavEntryComponentProps {
  navEntry: NavEntry;
  regulation: RegulationId;
}

const NavEntryComponent: React.FC<NavEntryComponentProps> = ({
  navEntry,
  regulation,
}) => {
  const { navigateToComplianceView } = useComplianceNavigator();
  const [sortConfig, setSortConfig] = React.useState<{
    key: 'fund_id' | 'fund_name' | 'status';
    direction: 'asc' | 'desc';
  }>({ key: 'status', direction: 'desc' });
  const [collapsedSubcategories, setCollapsedSubcategories] = React.useState<
    Set<string>
  >(new Set());

  const toggleSubcategory = (subcategory: string) => {
    setCollapsedSubcategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(subcategory)) {
        newSet.delete(subcategory);
      } else {
        newSet.add(subcategory);
      }
      return newSet;
    });
  };

  const handleSort = (key: 'fund_id' | 'fund_name' | 'status') => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getStatusPriority = (fund: {
    fund_id: string;
    fund_name: string;
    fund_name_full: string;
    fail: number;
    alert: number;
    pass: number;
  }) => {
    if (fund.fail > 0) return 3; // Highest priority
    if (fund.alert > 0) return 2;
    if (fund.pass > 0) return 1;
    return 0;
  };

  const sortFunds = (
    funds: Array<{
      fund_id: string;
      fund_name: string;
      fund_name_full: string;
      fail: number;
      alert: number;
      pass: number;
    }>,
  ) => {
    return [...funds].sort((a, b) => {
      if (sortConfig.key === 'status') {
        const statusA = getStatusPriority(a);
        const statusB = getStatusPriority(b);
        if (statusA !== statusB) {
          return sortConfig.direction === 'desc'
            ? statusB - statusA
            : statusA - statusB;
        }
      }

      if (sortConfig.key === 'fund_id') {
        const result = sortFundNames(a.fund_id, b.fund_id);
        return sortConfig.direction === 'asc' ? result : -result;
      }

      if (sortConfig.key === 'fund_name') {
        const result = a.fund_name.localeCompare(b.fund_name);
        return sortConfig.direction === 'asc' ? result : -result;
      }

      return 0;
    });
  };

  return (
    <div className="mr-2 space-y-4">
      {Object.entries(navEntry.fullData).map(([compliance, summary]) => {
        const totalFunds =
          summary.Fail.count + summary.Alert.count + summary.Pass.count;
        if (totalFunds === 0) return null;

        return (
          <div
            key={compliance}
            className="rounded-lg border border-gray-200 bg-white"
          >
            <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-1 text-xs outline outline-1 ${getComplianceSubcategoryColor(
                      compliance,
                    )}`}
                  >
                    {compliance}
                  </span>
                  <span className="text-sm font-semibold text-gray-800">
                    • {totalFunds} fund{totalFunds !== 1 ? 's' : ''}
                  </span>
                  <StatusPillGroup
                    data={{
                      Fail: summary.Fail.count,
                      Alert: summary.Alert.count,
                      Pass: summary.Pass.count,
                    }}
                  />
                </div>
                <button
                  onClick={() => toggleSubcategory(compliance)}
                  className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-gray-200"
                  title={
                    collapsedSubcategories.has(compliance)
                      ? 'Expand'
                      : 'Collapse'
                  }
                >
                  {collapsedSubcategories.has(compliance) ? (
                    <svg
                      className="h-4 w-4 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-4 w-4 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {!collapsedSubcategories.has(compliance) && (
              <div className="p-4">
                {/* Funds Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th
                          className="cursor-pointer select-none px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-50"
                          onClick={() => handleSort('fund_id')}
                        >
                          <div className="flex items-center gap-1">
                            Fund ID
                            {sortConfig.key === 'fund_id' && (
                              <span className="text-gray-400">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th
                          className="cursor-pointer select-none px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-50"
                          onClick={() => handleSort('fund_name')}
                        >
                          <div className="flex items-center gap-1">
                            Fund Name
                            {sortConfig.key === 'fund_name' && (
                              <span className="text-gray-400">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th
                          className="cursor-pointer select-none px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-50"
                          onClick={() => handleSort('status')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            Status
                            {sortConfig.key === 'status' && (
                              <span className="text-gray-400">
                                {sortConfig.direction === 'desc' ? '↓' : '↑'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(() => {
                        // Get all unique funds from this subcategory
                        const allFunds = new Map<
                          string,
                          {
                            fund_id: string;
                            fund_name: string;
                            fund_name_full: string;
                            fail: number;
                            alert: number;
                            pass: number;
                          }
                        >();

                        // Collect all funds and their status counts
                        (['Fail', 'Alert', 'Pass'] as const).forEach(
                          (status) => {
                            const summaryData =
                              summary[status as keyof FundSummaryWithTypes];
                            if (summaryData && summaryData.funds) {
                              summaryData.funds.forEach((fund) => {
                                const existing = allFunds.get(fund.fund_id);
                                if (existing) {
                                  existing[
                                    status.toLowerCase() as
                                      | 'fail'
                                      | 'alert'
                                      | 'pass'
                                  ] = 1;
                                } else {
                                  allFunds.set(fund.fund_id, {
                                    fund_id: fund.fund_id,
                                    fund_name: fund.fund_name,
                                    fund_name_full: fund.fund_name_full,
                                    fail: status === 'Fail' ? 1 : 0,
                                    alert: status === 'Alert' ? 1 : 0,
                                    pass: status === 'Pass' ? 1 : 0,
                                  });
                                }
                              });
                            }
                          },
                        );

                        return sortFunds(Array.from(allFunds.values())).map(
                          (fund) => (
                            <tr key={fund.fund_id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm text-gray-500">
                                {fund.fund_name}
                              </td>
                              <td className="px-3 py-2 text-sm font-medium text-gray-900">
                                {fund.fund_name_full}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {(() => {
                                  // Determine the primary status for this fund
                                  if (fund.fail > 0) {
                                    return <StatusPill status="Fail" />;
                                  } else if (fund.alert > 0) {
                                    return <StatusPill status="Alert" />;
                                  } else if (fund.pass > 0) {
                                    return <StatusPill status="Pass" />;
                                  }
                                  return (
                                    <span className="text-gray-400">-</span>
                                  );
                                })()}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  onClick={() =>
                                    navigateToComplianceView(
                                      regulation.id,
                                      compliance,
                                      {
                                        date: navEntry.date,
                                        id: fund.fund_name,
                                      },
                                    )
                                  }
                                  className="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                                >
                                  <FaExternalLinkAlt className="mr-1 h-3 w-3" />
                                  View
                                </button>
                              </td>
                            </tr>
                          ),
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
